import React from 'react';
import { Row, Title, QuestionNumberBadge } from 'pages/new-questions/styles/common';
import {
  StepBadgesRow,
  StepIndicatorBadgeDecision,
  HeaderInfo,
  ProgressWrapper,
  ProgressHeader,
  ProgressTrack,
  ProgressFill,
} from './styles';
import DecisionTreeIcon from 'img/decision-tree.svg';
import {
  UserStepAnswer,
  StepType,
} from 'pages/new-questions/components/DecisionTreeQuestion/utils/types';
import {
  getOptionLabelDecision,
  getStepBadgeClassName,
} from 'pages/new-questions/components/DecisionTreeQuestion/utils/utils';

interface QuestionHeaderProps {
  questionNumber?: number;
  totalQuestions?: number;
  answeredQuestionsCount?: number;
  caseCode?: string;
  showReviewBadges?: boolean;
  userAnswers?: UserStepAnswer[];
  totalSteps?: number;
  currentStepIndex?: number;
  currentStepType?: StepType;
  handleSelectCurrentStep?: (index: number, type: string) => void;
}

export const QuestionHeader: React.FC<QuestionHeaderProps> = ({
  questionNumber,
  totalQuestions,
  answeredQuestionsCount,
  caseCode,
  showReviewBadges = false,
  userAnswers = [],
  totalSteps = 0,
  currentStepIndex = 0,
  currentStepType,
  handleSelectCurrentStep,
}) => {
  const isFinalized = showReviewBadges;
  const answeredCount = userAnswers.length;

  const getProgressValues = () => {
    // Contexto de prova/simulado com contagem de respostas efetivas
    if (answeredQuestionsCount !== undefined && totalQuestions !== undefined) {
      return { current: answeredQuestionsCount, total: totalQuestions };
    }

    // Contexto de revisão (review) — mostra steps respondidos
    if (isFinalized) {
      return { current: answeredCount, total: answeredCount };
    }

    // Fallback para decision tree standalone (preview/steps internos)
    return {
      current: Math.min(totalSteps || 0, Math.max(currentStepIndex + 1, answeredCount)),
      total: totalSteps ?? 0,
    };
  };

  const { current: progressCurrent, total: progressTotal } = getProgressValues();

  const hasProgress = progressTotal > 0;
  const progressPercent = hasProgress
    ? Math.min(100, Math.max(0, (progressCurrent / progressTotal) * 100))
    : 0;

  const getBadgeClassName = (answer: UserStepAnswer | null, index: number): string => {
    if (showReviewBadges && answer) {
      return getStepBadgeClassName(answer.isCorrect);
    }

    const isActive =
      answer && answer.stepIndex === currentStepIndex && answer.stepType === currentStepType;

    if (isActive) {
      return 'is-active';
    }

    if (answer) {
      return 'is-answered';
    }

    return '';
  };

  const getBadgeContent = (answer: UserStepAnswer | null, index: number): string => {
    const label = getOptionLabelDecision(index);
    if (showReviewBadges && answer) {
      return `${label}${answer.isCorrect ? ') 1pt' : ') 0pt'}`;
    }

    return label;
  };

  const badgeCount = isFinalized ? answeredCount : Math.max(totalSteps, answeredCount);

  return (
    <Row padding="0 24px" width="100%">
      <HeaderInfo>
        <Title>
          {questionNumber !== undefined && totalQuestions !== undefined && (
            <QuestionNumberBadge>
              <p>
                {questionNumber}/{totalQuestions}
              </p>
            </QuestionNumberBadge>
          )}
          <img src={DecisionTreeIcon} alt="Decision Tree icon" width={24} height={24} />
          <p>Caso: {caseCode}</p>
        </Title>

        {hasProgress && (
          <ProgressWrapper>
            <ProgressHeader>
              <span>Progresso</span>
              <span>
                {progressCurrent} / {progressTotal}
              </span>
            </ProgressHeader>
            <ProgressTrack aria-hidden="true">
              <ProgressFill widthPercent={progressPercent} />
            </ProgressTrack>
          </ProgressWrapper>
        )}
      </HeaderInfo>

      {badgeCount > 0 && (
        <StepBadgesRow>
          {Array.from({ length: badgeCount }).map((_, index) => {
            const answer = userAnswers[index] || null;
            const hasAnswer = answer !== null;
            return (
              <StepIndicatorBadgeDecision
                key={index}
                className={hasAnswer ? getBadgeClassName(answer, index) : ''}
                onClick={() => {
                  if (hasAnswer && answer) {
                    handleSelectCurrentStep?.(answer.stepIndex, answer.stepType);
                  }
                }}
                style={{ cursor: hasAnswer ? 'pointer' : 'default', opacity: hasAnswer ? 1 : 0.4 }}
              >
                {hasAnswer ? getBadgeContent(answer, index) : getOptionLabelDecision(index)}
              </StepIndicatorBadgeDecision>
            );
          })}
        </StepBadgesRow>
      )}
    </Row>
  );
};
